/**
 * /sign-in — email + password, Google SSO, MFA, and client-trust
 * verification.
 *
 * Flow (see `~/.kilocode/skills/clerk-custom-ui/core-3/custom-sign-in.md`):
 *   1. Password path:
 *      `signIn.create({ identifier, password })` → branch on status:
 *      - `complete` → `setActive({ session })` → navigate to
 *        `search.redirect ?? '/'`
 *      - `needs_second_factor` → `/two-factor?flow=sign-in`
 *      - `needs_client_trust` → prepare email-code first factor and
 *        navigate to `/verify-sign-in` (new-device check)
 *      - `needs_new_password` → `/reset-password` (rare; reset flow)
 *      - anything else → surface Clerk's error
 *   2. Google path:
 *      `signIn.authenticateWithRedirect({ strategy: 'oauth_google',
 *      redirectUrl: '/sso-callback', redirectUrlComplete })`. Clerk
 *      drives the OAuth dance and lands on `/sso-callback`, which
 *      mounts `<AuthenticateWithRedirectCallback />`. New users
 *      land on `/onboarding`; existing users on `search.redirect`.
 */

import { AlreadySignedInGate } from '@/components/shell/AlreadySignedInGate';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  activateSession,
  prepareClientTrustEmailCode,
  signInWithOAuth,
  signInWithPassword,
} from '@/lib/auth';
import { useSignIn } from '@clerk/react/legacy';
import { Eye, EyeSlash, GoogleLogo } from '@phosphor-icons/react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_auth/sign-in')({
  component: SignInPage,
  validateSearch: (search): { redirect?: string; error?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
});

function SignInPage(): ReactElement {
  const { redirect } = Route.useSearch();
  return (
    <AlreadySignedInGate redirectTo={redirect ?? '/'}>
      <SignInForm />
    </AlreadySignedInGate>
  );
}

function SignInForm(): ReactElement {
  const { signIn, setActive, isLoaded } = useSignIn();
  const navigate = useNavigate();
  const { redirect, error: searchError } = Route.useSearch();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [oauthSubmitting, setOauthSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isLoaded || !signIn || !setActive) return;

    setSubmitting(true);
    setFieldErrors({});
    setFormError(undefined);

    const result = await signInWithPassword({ signIn, identifier: email, password });

    if (result.kind === 'error') {
      setFieldErrors({
        emailAddress: result.fieldErrors.identifier ?? result.fieldErrors.emailAddress,
        password: result.fieldErrors.password,
      });
      setFormError(result.formError);
      setSubmitting(false);
      return;
    }

    if (result.kind === 'complete') {
      await activateSession({
        setActive,
        sessionId: result.sessionId,
        redirectUrl: redirect,
      });
      return;
    }

    if (result.kind === 'needs_second_factor') {
      navigate({ to: '/two-factor', search: { flow: 'sign-in', redirect } });
      return;
    }

    if (result.kind === 'needs_client_trust' || result.kind === 'needs_first_factor') {
      // Two paths land here:
      //   1. `needs_client_trust` (Core 3): password verified but the
      //      device is untrusted + MFA is off. Kick off the email-code
      //      first factor so the user sees a code already in their
      //      inbox on /verify-sign-in.
      //   2. `needs_first_factor`: the instance's primary strategy is
      //      a first-factor code (e.g. passwordless / email-first
      //      setups). Same prepare + attempt pair.
      const prep = await prepareClientTrustEmailCode(signIn);
      if (prep.kind === 'error') {
        setFormError(prep.formError ?? 'Unable to send verification code. Try again.');
        setSubmitting(false);
        return;
      }
      navigate({ to: '/verify-sign-in', search: { redirect } });
      return;
    }

    if (result.kind === 'needs_new_password') {
      navigate({ to: '/reset-password' });
      return;
    }

    setFormError('Sign-in is in an unexpected state. Try again.');
    setSubmitting(false);
  }

  async function onGoogle() {
    if (!isLoaded || !signIn || oauthSubmitting) return;
    setOauthSubmitting(true);
    setFormError(undefined);
    // `redirectUrlComplete` encodes the eventual destination the user
    // was trying to reach (passed through as `?redirect=` on the
    // sign-in URL). Clerk's callback component honours
    // `signInFallbackRedirectUrl` which we set to this value.
    const result = await signInWithOAuth({
      signIn,
      strategy: 'oauth_google',
      redirectUrl: `/sso-callback${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`,
      redirectUrlComplete: redirect ?? '/',
    });
    // Normally the browser has already navigated by now. If we
    // reach here it's because Clerk refused the request (e.g.
    // `oauth_google` not enabled on the instance).
    if (result.kind === 'error') {
      setFormError(result.formError ?? 'Google sign-in is unavailable right now.');
      setOauthSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-2 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Sign in</h1>
        <p className="text-sm text-[var(--color-mute)]">
          Clickweave is in beta — currently invitation-only. Existing users sign in below; new users
          need an invite link from your email.
        </p>
      </header>

      {searchError === 'access_denied' && (
        <Alert variant="warning">
          <AlertTitle>Invitation required</AlertTitle>
          <AlertDescription>
            Clickweave is in beta — currently invitation-only. Your Google sign-in worked, but this
            account isn't yet enrolled here. Ask an admin to invite you, then click the invite link
            in your email to finish setting up.
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        <Button
          type="button"
          variant="outline"
          className="w-full gap-2"
          onClick={onGoogle}
          disabled={oauthSubmitting || submitting}
        >
          <GoogleLogo aria-hidden="true" size={16} weight="bold" />
          {oauthSubmitting ? 'Redirecting…' : 'Continue with Google'}
        </Button>
        <div className="relative flex items-center">
          <div className="flex-1 border-t border-[var(--color-line)]" />
          <span className="px-3 text-xs uppercase tracking-wide text-[var(--color-mute)]">or</span>
          <div className="flex-1 border-t border-[var(--color-line)]" />
        </div>
      </div>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail((event.target as HTMLInputElement).value)}
            aria-invalid={fieldErrors.emailAddress ? true : undefined}
          />
          {fieldErrors.emailAddress && <FieldError>{fieldErrors.emailAddress}</FieldError>}
        </Field>

        <Field>
          <div className="flex w-full items-center justify-between">
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-[var(--color-accent-brand)] hover:underline"
            >
              Forgot?
            </Link>
          </div>
          <div className="relative w-full">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.password ? true : undefined}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-2 flex items-center text-[var(--color-mute)] hover:text-[var(--color-ink)]"
            >
              {showPassword ? (
                <EyeSlash aria-hidden="true" size={16} />
              ) : (
                <Eye aria-hidden="true" size={16} />
              )}
            </button>
          </div>
          {fieldErrors.password && <FieldError>{fieldErrors.password}</FieldError>}
        </Field>

        {formError && (
          <div
            className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
            role="alert"
          >
            {formError}
          </div>
        )}

        {/* Clerk's bot-sign-up CAPTCHA target (sign-in flows don't
            currently use this element — docs scope it to sign-up —
            but leaving it in place is zero-cost and future-proof if
            Clerk ever extends Attack Protection to sign-in). */}
        <div id="clerk-captcha" />

        <Button type="submit" className="w-full" disabled={submitting || oauthSubmitting}>
          {submitting ? 'Signing in…' : 'Continue'}
        </Button>
      </form>

      <p className="text-center text-sm text-[var(--color-mute)]">
        No account?{' '}
        <Link
          to="/sign-up"
          className="font-medium text-[var(--color-accent-brand)] hover:underline"
        >
          Sign up
        </Link>
      </p>
    </div>
  );
}
